'use client';
import { useState, useEffect } from 'react';
import { Poppins } from 'next/font/google';
import TitleMain from '@/components/shared/TitleMain/TitleMain';

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['400', '600'],
});

function calculateExperience(startDate) {
  const now = new Date();
  let years = now.getFullYear() - startDate.getFullYear();
  let months = now.getMonth() - startDate.getMonth();
  let days = now.getDate() - startDate.getDate();

  // Adjust if days are negative
  if (days < 0) {
    months -= 1;
    // Get the number of days in the previous month
    const previousMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += previousMonth.getDate();
  }
  // Adjust if months are negative
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return { years, months, days };
}

const AboutMe = () => {
  const [experience, setExperience] = useState({
    years: 0,
    months: 0,
    days: 0,
  });

  useEffect(() => {
    const startDate = new Date('2024-01-27');
    setExperience(calculateExperience(startDate));
  }, []);

  return (
    <section
      id='About'
      className={`relative min-h-screen bg-fixed bg-cover bg-center bg-hero ${poppins.className}`}
    >
      {/* Parallax Overlay */}
      <div className='absolute inset-0 bg-black/10 backdrop-blur-sm' />

      {/* Content Container */}
      <div className='relative h-full min-h-screen container mx-auto grid lg:grid-cols-2 gap-12 items-center'>
        {/* Image Section - Visible on larger screens */}
        <div className='hidden lg:block relative h-96 rounded-2xl overflow-hidden transform hover:scale-105 transition-transform duration-300'>
          <div className='absolute inset-0 bg-blue-500 bg-opacity-0 ' />
        </div>

        {/* Text Content */}
        <div className='p-4 rounded-md h-max bg-opacity-70 bg-white backdrop-blur-lg z-20'>
          <div className='space-y-8 text-gray-900 blur-none z-40'>
            <TitleMain text={'About me'} />

            <p className='text-lg md:text-xl leading-relaxed text-gray-900'>
              I am a passionate MERN stack developer and Computer Science
              student from Bangladesh with{' '}
              <span className='font-semibold text-blue-700'>
                {experience.years} year{experience.years !== 1 && 's'}{' '}
                {experience.months} month{experience.months !== 1 && 's'}{' '}
                {experience.days} day{experience.days !== 1 && 's'}
              </span>{' '}
              of professional experience. Since beginning my web development
              journey in 2021, I've dedicated myself to transforming complex
              challenges into elegant digital solutions.
            </p>

            <div className='space-y-4'>
              <h3 className='text-2xl font-semibold text-gray-900'>
                Key Projects:
              </h3>
              <ul className='space-y-3 pl-4 border-l-4 border-blue-400'>
                <li className='flex items-center'>
                  <span className='mr-2 text-blue-700'>▹</span>
                  E-commerce platform with real-time inventory management
                </li>
                <li className='flex items-center'>
                  <span className='mr-2 text-blue-700'>▹</span>
                  Appointment booking system with calendar integration
                </li>
                <li className='flex items-center'>
                  <span className='mr-2 text-blue-700'>▹</span>
                  Real-time chat application with message history
                </li>
                <li className='flex items-center'>
                  <span className='mr-2 text-blue-700'>▹</span>
                  Restaurant management system with table reservations
                </li>
                <li className='flex items-center'>
                  <span className='mr-2 text-blue-700'>▹</span>
                  Dynamic DOCX to HTML converter with style preservation
                </li>
              </ul>
            </div>

            <p className='text-lg md:text-xl leading-relaxed text-gray-900'>
              My goal is to evolve into a senior developer role within five
              years, continuously adapting to emerging technologies while
              maintaining expertise in my core stack. I thrive on creating
              impactful solutions and welcome opportunities to collaborate on
              meaningful projects.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutMe;
